/*
 *  Links List - Create a list of links, and then share it!
 *  Copyright (c) Luke Denton
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation; either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import firebase from 'firebase/app';
import 'firebase/auth';
import { Fragment } from 'vue-fragment';
import Analytics, { AUTHENTICATION_LOGOUT } from "@/utils/Analytics";
import {
  auth,
  listenForBookmarks,
  listenForListChange,
  listenForLists,
  stopListeningForListChange
} from "@/utils/FirebaseListeners";
import { subscribe } from "@/utils/NetworkCalls";
import { ALERT_SUCCESS, ALERT_ERROR } from "@constants";
import store from "@store";

export default {
  name: 'UserAccount',

  components: {
    Fragment
  },

  data() {
    return {
      visibleModal: '',
      isSubscribingToPremium: false,
      isSubscribingToPremiumSuccess: false,
      verificationState: 'default',
      isEditingEmail: false,
      isEditingPassword: false,
      isPasswordUpdating: false,
      isConfirmPasswordRequired: false,
      isConfirmingPassword: false,
      callbackAfterPasswordReEnter: null,
      loadingList: false,
      error: {
        general: false,
        password: false,
        message: '',
        passwordMatch: false
      },
      authenticatedOptions: [
        {
          text: 'My Details',
          callback: () => {
            this.visibleModal = this.MODAL__DETAILS;
          }
        },
        {
          text: 'My Lists',
          callback: this.handleDisplayMyLists
        },
        {
          text: 'My Bookmarks',
          callback: this.handleDisplayMyBookmarks
        },
        {
          text: '*Premium',
          callback: () => {
            this.visibleModal = this.MODAL__PREMIUM;
          }
        },
        {
          text: 'Sign Out',
          callback: () => {
            Analytics.FireAuthentication(AUTHENTICATION_LOGOUT);
            auth.signOut().then(() => {
              this.$store.dispatch('signOut');
            });
          }
        }
      ]
    };
  },

  created() {
    this.MODAL__DETAILS = 'details';
    this.MODAL__MY_LISTS = 'my_lists';
    this.MODAL__SUBSCRIBED_LISTS = 'subscribed_lists';
    this.MODAL__PREMIUM = 'premium';
  },

  computed: {
    theme() {
      return this.$store.getters['ui/theme'];
    },
    user() {
      return this.$store.getters.user;
    },
    subscribedLists() {
      // Filter them here so we can determine if a user is already subscribed to the list.
      // If we filter out the current list ID from the savedLists at the time we save them in the store,
      // then we won't be able to check if the user _is_ subscribed to the current list or not
      return this.$store.getters.subscribedLists.filter(list => list.id !== this.$store.getters.urlString);
    },
    isEmailVerified() {
      return this.$store.getters.user.emailVerified;
    }
  },

  methods: {
    handleNavigateToList(id) {
      this.$router.push({ path: `${id}` })
      this.visibleModal = '';
    },

    async handleSubscribeToPremium() {
      this.isSubscribingToPremium = true;
      await subscribe(this.$store.getters.user.email, true);
      this.subscribeEmail = '';
      this.isSubscribingToPremiumSuccess = true;
      this.isSubscribingToPremium = false;
    },

    sendVerificationEmail: function () {
      this.verificationState = 'sending';
      auth.currentUser.sendEmailVerification().then(() => {
        this.verificationState = 'sent';
        auth.signOut().then(() => {
          this.$store.dispatch('alerts/displayAlert', { type: ALERT_SUCCESS, message: 'Verification Email Sent. Please check inbox to complete verification, then come back to log in' });
          window.setTimeout(() => {
            store.dispatch('signOut');
          }, 5000)
        });
      })
    },

    handleCloseDetailsModal() {
      this.isEditingEmail = false;
      this.visibleModal = '';
    },

    handleSaveUserDetails: function(e) {
      this.isEditingEmail = false;
      const formElements = e.target.elements;
      const email = formElements.email.value;
      this.callbackAfterPasswordReEnter = () => {
        return new Promise((res) => {
          auth.currentUser.updateEmail(email).then(() => {
            auth.currentUser.sendEmailVerification().then(() => {
              this.verificationState = 'sent';
              auth.signOut().then(() => {
                this.$store.dispatch('alerts/displayAlert', { type: ALERT_SUCCESS, message: 'Email address has been updated, please check inbox for verification link, then come back to log in using your new email address' });
                window.setTimeout(() => {
                  store.dispatch('signOut');
                }, 5000)
              });
            });
            this.isEditingEmail = false;
            res();
          }).catch((e) => {
            res();
            this.error.general = e.message;
          });
        })
      };
      this.isConfirmPasswordRequired = true;
    },

    handleConfirmPassword: function(e) {
      this.isConfirmingPassword = true;
      const formElements = e.target.elements;
      const password = formElements.password.value;
      const credential =  firebase.auth.EmailAuthProvider
        .credential(auth.currentUser.email, password);

      auth.currentUser.reauthenticateWithCredential(credential).then(() => {
        this.isConfirmPasswordRequired = false;
        this.callbackAfterPasswordReEnter().then(() => {
          this.isConfirmingPassword = false;
          this.callbackAfterPasswordReEnter = null;
        });
      }).catch((e) => {
        this.isConfirmingPassword = false;
        this.error.password = e.message;
      })
    },

    changePassword: function(e) {
      this.error.general = false;
      this.error.password = false;
      this.error.passwordMatch = false;

      this.isPasswordUpdating = true;
      const currentPassword = e.target.password.value;
      const newPassword = e.target['new-password'].value;
      const confirmPassword = e.target['confirm-password'].value;
      const passwordsMatch = newPassword === confirmPassword;
      const credential =  firebase.auth.EmailAuthProvider
        .credential(auth.currentUser.email, currentPassword);

      if (!passwordsMatch) {
        this.isPasswordUpdating = false;
        this.error.passwordMatch = "Passwords don't match";
      }

      auth.currentUser.reauthenticateWithCredential(credential).then(() => {
        if (!passwordsMatch) return;

        auth.currentUser.updatePassword(newPassword).then(() => {
          this.$store.dispatch('alerts/displayAlert', { type: ALERT_SUCCESS, message: 'Password has been updated' });
          this.isPasswordUpdating = false;
          this.isEditingPassword = false;
        }).catch((error) => {
          this.isPasswordUpdating = false;
          this.error.general = 'An error occurred. Please try again, and if problems persist, please contact us.';
        });
      }).catch((e) => {
        this.isPasswordUpdating = false;
        this.error.password = 'Password incorrect';
      })
    },

    handleDisplayMyLists: async function() {
      this.visibleModal = this.MODAL__MY_LISTS;
      this.loadingList = true;
      await listenForLists(this.$store.getters.user.uid);
      this.loadingList = false;
    },

    handleDisplayMyBookmarks: async function() {
      this.visibleModal = this.MODAL__SUBSCRIBED_LISTS;
      this.loadingList = true;
      await listenForBookmarks(this.$store.getters.user.uid);
      this.loadingList = false;
    }

  }
};
